import React, {useEffect, useRef, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {Typography} from '@mui/material';
import Button from 'components/ui/Button';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/react';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import {
  useGetAvailableSlotsQuery,
  useGetCustomerInsuranceQuery,
  useGetLocationsQuery,
  useUpdateBookingSlotMutation
} from 'services/locations';
import Spinner from 'components/ui/Spinner';
import AutoComplete, {OptionType} from 'components/ui/AutoComplete';
import useMedia from 'use-media';
import {convertTo12HourFormat} from "../../../utils/convertTo12HourFormat";
import {enqueueSnackbar} from "notistack";
import * as Styles from 'layouts/PrivateLayout.styles';
import {useGetBookingSlotQuery} from "../../../services/bookingSlots";

const EditBookingDetails = () => {
  const { orderId = '', bookingId = '' } = useParams();
  const isMobile = useMedia({ maxWidth: '640px' });
  const calendarRef = useRef<any>(null);
  const [locationsSelected, setLocationsSelected] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{ startTime: string, endTime: string } | null>(null);
  const [currentDate, setCurrentDate] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [classId, setClassId] = useState<string | null>(null);
  const [eventsCalendar, setEventsCalendar] = useState<any[]>([]);

  const navigate = useNavigate();
  const [updateBookingSlot, { isLoading: isUpdating }] = useUpdateBookingSlotMutation();

  const {
    data: bookingSlot,
    isFetching: isFetchingBookingSlot,
    isLoading: isLoadingBookingSlot,
    refetch: refetchBookingSlot
  } = useGetBookingSlotQuery(bookingId);

  const { data: availableSlots, isLoading: isLoadingSlots } = useGetAvailableSlotsQuery(
      { locationId: selectedLocationId!, date: selectedDate! },
      { skip: !selectedLocationId || !selectedDate }
  );

  const { data: dataLocations, isLoading: isLoadingLocations } = useGetLocationsQuery({
    limit: 999
  });

  const { data: insuranceData, isLoading: isLoadingInsurance } = useGetCustomerInsuranceQuery(customerId!, {
    skip: !customerId
  });

  useEffect(() => {
    if (bookingSlot?.booking_slot) {
      setCustomerId(bookingSlot.booking_slot.customer_id);
      setClassId(bookingSlot.booking_slot?.class?.id);
      setSelectedLocationId(bookingSlot.booking_slot.location_id);

      const locationFromBooking = dataLocations?.locations?.find(
          (loc: any) => loc.id === bookingSlot.booking_slot.location_id
      );
      if (locationFromBooking) {
        setLocationsSelected({
          label: locationFromBooking.name,
          id: locationFromBooking.id
        });
      }
    }
  }, [bookingSlot, dataLocations]);


  useEffect(() => {
    if (customerId && !isLoadingInsurance) {
      console.log("Checking insurance for customerId:", customerId);
      const isInsuranceValid = checkInsuranceValidity(insuranceData);
      if (!isInsuranceValid) {
        console.log("Insurance is invalid. Redirecting to purchase insurance.");
        enqueueSnackbar("Insurance is required for rescheduling. Redirecting to purchase insurance.", { variant: 'warning' });
        try {
          navigate('/admin/order/create', {
            state: {
              insuranceFeeRequired: true,
              orderId,
              bookingId,
              originalBookingSlotId: bookingId,
              originalClassId: classId
            }
          });
        } catch (error) {
          console.error("Navigation error:", error);
          enqueueSnackbar("An error occurred while redirecting. Please try again.", { variant: 'error' });
        }
      }
    }
  }, [customerId, insuranceData, isLoadingInsurance, classId, navigate, orderId, bookingId]);

  const checkInsuranceValidity = (insurance: any) => {
    console.log("Checking insurance validity:", insurance);
    if (!insurance?.customer_insurance?.is_insured) {
      console.log("Insurance is not valid: Customer is not insured");
      return false;
    }
    if (insurance?.customer_insurance?.expiration_date) {
      const expirationDate = new Date(insurance.customer_insurance.expiration_date);
      if (expirationDate < new Date()) {
        console.log("Insurance is not valid: Insurance has expired");
        return false;
      }
    }
    console.log("Insurance is valid");
    return true;
  };

  const handleSlotSelection = (slot: { startTime: string, endTime: string }) => {
    setSelectedSlot(slot);
  };

  const handleUpdate = async () => {
    if (!selectedLocationId || !selectedSlot || !bookingId) {
      enqueueSnackbar("Please select a location, date, and time slot", {
        variant: 'error', autoHideDuration: 1000
      });
      return;
    }

    const dateStart = new Date(`${selectedDate} ${selectedSlot.startTime}`).toISOString();
    const dateEnd = new Date(`${selectedDate} ${selectedSlot.endTime}`).toISOString();

    try {
      await updateBookingSlot({
        locationId: selectedLocationId,
        bookingId,
        body: {
          date_start: dateStart,
          date_end: dateEnd
        }
      }).unwrap();

      enqueueSnackbar("Successfully rescheduled!", {
        variant: 'success', autoHideDuration: 1000
      });
      navigate(`/admin/orders/${orderId}/booking-details/${bookingId}`);
    } catch (error) {
      console.error("Failed to update booking", error);
      enqueueSnackbar("There was an issue rescheduling, please close this and try again!", {
        variant: 'error', autoHideDuration: 1000
      });
    }
  };

  const handleSelect = (info: any) => {
    const clickedDate = info.dateStr;
    setSelectedDate(clickedDate);
  };

  const handleDateClick = (arg: any) => {
    if (!locationsSelected) {
      enqueueSnackbar("Please select a location first", { variant: 'warning' });
      return;
    }

    const clickedDate = arg.dateStr;
    setCurrentDate(clickedDate);
    setSelectedDate(clickedDate);
    setSelectedLocationId(locationsSelected.id.toString());
  };

  const locations: OptionType[] = dataLocations?.locations?.map((row: any) => ({
    label: row.name,
    id: row.id
  })) || [];

  const handleLocationsChange = (_event: React.ChangeEvent<{}>, value: any) => {
    console.log(value);
    setLocationsSelected(value);
    setSelectedLocationId(value?.id.toString() || null);
  };

  if (isLoadingBookingSlot || isLoadingLocations || isLoadingInsurance) {
    return <Spinner />;
  }

  if (!bookingSlot?.booking_slot) {
    return <div>No booking slot data available. Please try again later.</div>;
  }

  if (!dataLocations?.locations) {
    return <div>No locations data available. Please try again later.</div>;
  }
  return (
      <>
        <div className="grid grid-cols-1 lg:grid-cols-2 h-full container mx-auto">
          <div className="flex flex-col gap-10 border-r-2 p-6">
            <section>
              <AutoComplete
                  id="locations"
                  label="Locations"
                  placeholder="Select location"
                  value={locationsSelected}
                  onChange={handleLocationsChange}
                  options={locations}
                  limitTags={isMobile ? 5 : 20}
                  multipleOptions={false}
              />

            </section>
            <section>
              <FullCalendar
                  ref={calendarRef}
                  plugins={[interactionPlugin, dayGridPlugin, timeGridPlugin]}
                  initialView="dayGridMonth"
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth'
                  }}
                  select={handleSelect}
                  selectable
                  events={eventsCalendar}
                  dateClick={handleDateClick}
                  longPressDelay={100}
              />
            </section>
          </div>
          <div className="flex flex-col gap-2 p-6 pb-32">
            <section>
              <Typography sx={{ fontSize: 18 }}>Open Slots for date: {currentDate}</Typography>
            </section>
            {isLoadingSlots ? (
                <Spinner />
            ) : availableSlots?.available_slots?.length ? (
                availableSlots.available_slots.map((slot: any) => (
                    <Button
                        key={slot.id}
                        startIcon={<CalendarTodayOutlined />}
                        cc-width="full"
                        onClick={() => handleSlotSelection(slot)}
                        cc-color={selectedSlot === slot ? "purple" : undefined}
                    >
                      {convertTo12HourFormat(slot.startTime)} - {convertTo12HourFormat(slot.endTime)}
                    </Button>
                ))
            ) : (
                <Typography>No available slots for the selected date</Typography>
            )}
          </div>
        </div>
        <Styles.FlexContainer flex-row-lg className="border-t-2 pt-4">
          <Styles.FlexContainerColumn push-right className="pr-4">
            <Link to={`/admin/orders/${orderId}/booking-details/${bookingId}`}>
              <Button>Cancel</Button>
            </Link>
            <span className="mr-4"></span>
            <Button
                cc-color="purple"
                onClick={handleUpdate}
                disabled={!selectedSlot || isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Update'}
            </Button>
          </Styles.FlexContainerColumn>
        </Styles.FlexContainer>
      </>
  );
};

export default EditBookingDetails;
